import { LucideIcon, ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { HelpCircle } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

export interface MetricComparison {
  percent: number;
  direction: 'up' | 'down' | 'neutral';
  label: string;
  /** Set to false for metrics where an increase is bad (e.g., costs) */
  isPositiveGood?: boolean;
}

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: LucideIcon;
  valueColor?: string;
  iconBgColor?: string;
  /** Icon colour inside the tile; pair with iconBgColor (e.g. text-success + bg-success/10) */
  iconColor?: string;
  tooltip?: string;
  /** Optional comparison data for period-over-period display */
  comparison?: MetricComparison;
  /** Show skeleton while comparison is loading */
  comparisonLoading?: boolean;
  onClick?: () => void;
  className?: string;
}

export const MetricCard = ({
  title,
  value,
  subtitle,
  icon: Icon,
  valueColor = 'text-foreground',
  iconBgColor = 'bg-primary/10',
  iconColor = 'text-primary',
  tooltip,
  comparison,
  comparisonLoading,
  onClick,
  className,
}: MetricCardProps) => {
  // Determine comparison color based on direction and whether positive is good
  const getComparisonColor = () => {
    if (!comparison || comparison.direction === 'neutral') return 'text-muted-foreground';

    const isPositiveGood = comparison.isPositiveGood !== false; // Default to true

    if (comparison.direction === 'up') {
      return isPositiveGood ? 'text-success' : 'text-destructive';
    } else {
      return isPositiveGood ? 'text-destructive' : 'text-success';
    }
  };

  const ComparisonIcon = comparison?.direction === 'up'
    ? ArrowUpRight
    : comparison?.direction === 'down'
      ? ArrowDownRight
      : Minus;

  const showComparisonRow = comparisonLoading || comparison;

  return (
    <Card
      className={cn(
        onClick &&
          'cursor-pointer transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
        className
      )}
      onClick={onClick}
      // A clickable tile has to be reachable and operable from the keyboard;
      // without this it is a div that only a mouse can use.
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-center gap-1">
            <p className="truncate text-xs font-medium text-muted-foreground">{title}</p>
            {tooltip && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-3 w-3 flex-shrink-0 cursor-help text-muted-foreground/60" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-xs">{tooltip}</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          <div
            className={cn(
              'flex h-[26px] w-[26px] flex-shrink-0 items-center justify-center rounded-md',
              iconBgColor
            )}
          >
            <Icon className={cn('h-3.5 w-3.5', iconColor)} />
          </div>
        </div>

        <p className={cn('mt-1 text-[26px] font-semibold leading-tight tracking-tight tabular-nums', valueColor)}>
          {value}
        </p>
        {subtitle && <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>}

        {/* Comparison footer — bordered row with delta in success/destructive */}
        {showComparisonRow && (
          <div className="mt-3 border-t pt-2.5">
            {comparisonLoading ? (
              <Skeleton className="h-3 w-20" />
            ) : comparison && comparison.direction !== 'neutral' ? (
              <div
                className={cn(
                  'flex items-center gap-1 text-xs font-medium animate-in fade-in slide-in-from-bottom-1 duration-300',
                  getComparisonColor()
                )}
              >
                <ComparisonIcon className="h-3 w-3" />
                <span className="truncate">
                  {comparison.percent.toFixed(1)}% {comparison.label}
                </span>
              </div>
            ) : comparison ? (
              <div className="flex items-center gap-1 text-xs text-muted-foreground animate-in fade-in duration-300">
                <Minus className="h-3 w-3" />
                <span className="truncate">No change {comparison.label}</span>
              </div>
            ) : null}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
